import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Eye, Edit, FileText, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminLegalEditor() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("privacy");
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requireReaccept, setRequireReaccept] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchContent(activeTab);
  }, [activeTab, user, navigate]);

  const fetchContent = async (pageType) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/legal/${pageType}`);
      setContent(response.data.content);
    } catch {
      setContent("");
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/legal/${activeTab}`,
        { content, require_reacceptance: requireReaccept },
        { withCredentials: true }
      );
      const msg = requireReaccept
        ? "Updated. All users will need to re-accept on next login."
        : "Updated successfully.";
      toast.success(msg);
      setRequireReaccept(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const renderMarkdown = (text) => {
    return text
      .split("\n")
      .map((line, i) => {
        if (line.startsWith("# ")) {
          return <h1 key={i} className="text-xl font-semibold text-ink tracking-tight mb-4 mt-6">{line.slice(2)}</h1>;
        }
        if (line.startsWith("## ")) {
          return <h2 key={i} className="text-lg font-semibold text-ink mb-2 mt-5">{line.slice(3)}</h2>;
        }
        if (line.startsWith("- ")) {
          const html = line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return <li key={i} className="ml-4 text-ink-muted mb-1 list-disc" dangerouslySetInnerHTML={{ __html: html }} />;
        }
        if (line.trim() === "") {
          return <div key={i} className="h-2" />;
        }
        const html = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <p key={i} className="text-ink-muted mb-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
      });
  };

  return (
    <div className="min-h-screen bg-app p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/admin/competitions")}
            className="text-ink-soft hover:text-ink-muted"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-ink">Legal Pages</h1>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-2 mb-4">
          <button
            data-testid="tab-privacy"
            onClick={() => { setActiveTab("privacy"); setPreview(false); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "privacy"
                ? "bg-ink text-white"
                : "bg-white text-ink-muted border border-line-strong hover:bg-app"
            }`}
          >
            <Shield className="w-4 h-4" />
            Privacy Policy
          </button>
          <button
            data-testid="tab-terms"
            onClick={() => { setActiveTab("terms"); setPreview(false); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "terms"
                ? "bg-ink text-white"
                : "bg-white text-ink-muted border border-line-strong hover:bg-app"
            }`}
          >
            <FileText className="w-4 h-4" />
            Terms of Use
          </button>
        </div>

        {/* Editor / Preview */}
        <div className="bg-white rounded-xl shadow-sm border border-line">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <div className="flex gap-2">
              <button
                data-testid="mode-edit"
                onClick={() => setPreview(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${
                  !preview ? "bg-surface-2 text-ink font-medium" : "text-ink-soft hover:text-ink-muted"
                }`}
              >
                <Edit className="w-3.5 h-3.5" />
                Edit
              </button>
              <button
                data-testid="mode-preview"
                onClick={() => setPreview(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${
                  preview ? "bg-surface-2 text-ink font-medium" : "text-ink-soft hover:text-ink-muted"
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                Preview
              </button>
            </div>
            <Button
              data-testid="save-legal-button"
              onClick={handleSave}
              disabled={saving || loading}
              className="bg-ink hover:bg-ink-hover flex items-center gap-2"
              size="sm"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>

          {/* Content Area */}
          <div className="p-4">
            {loading ? (
              <div className="text-center py-12 text-ink-soft">Loading...</div>
            ) : preview ? (
              <div className="min-h-[400px] prose prose-sm max-w-none">
                {renderMarkdown(content)}
              </div>
            ) : (
              <div>
                <p className="text-xs text-ink-soft mb-2">
                  Use <code className="bg-surface-2 px-1 rounded"># Heading</code> for titles,{" "}
                  <code className="bg-surface-2 px-1 rounded">## Section</code> for sections,{" "}
                  <code className="bg-surface-2 px-1 rounded">**bold**</code> for emphasis,{" "}
                  <code className="bg-surface-2 px-1 rounded">- item</code> for lists.
                </p>
                <textarea
                  data-testid="legal-editor-textarea"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full min-h-[400px] border border-line-strong rounded-lg p-4 text-sm font-mono text-ink focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent resize-y"
                  placeholder="Enter content using markdown formatting..."
                />
              </div>
            )}
          </div>

          {/* Re-acceptance toggle */}
          <div className="border rounded-xl p-4 bg-warm-tint/40 border-warm/40">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="require-reaccept-checkbox"
                type="checkbox"
                checked={requireReaccept}
                onChange={(e) => setRequireReaccept(e.target.checked)}
                className="w-4 h-4 rounded border-line-strong text-accent focus:ring-accent"
              />
              <span className="text-sm text-ink-muted">
                Require all users to re-accept on next login
              </span>
            </label>
            {requireReaccept && (
              <p className="text-xs text-warm mt-1.5 ml-7">
                Every user will see the acceptance screen before they can use the app.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
